import { PHASE } from './phases.js';
import { configForState, isAdvancedMode } from './modeConfig.js';
import { SLOTS_CUMULATIVE_BY_YEAR } from '../data/slots.js';
import { DISASTER_DRAWS_PER_YEAR } from '../data/constants.js';
import { ADVANCED_CARBON_YEAR_WEIGHTS } from '../data/advancedCapacity.js';
import { lookupCarbonBracket } from '../engine/carbonTax.js';
import { computeYearOpex, totalYearOpex } from '../engine/opex.js';
import { outputMultiplierFactorForPlant } from '../engine/disasters.js';
import {
  decommissionFeeForPlant,
  requiredFirmCapacity,
  upliftedFirmCapacityUnits,
  firmCapacityCountWeights,
  variableDiversityUplift,
  variableFamiliesOwned,
  firmFactorFromGenerationFactor,
  expectedPortfolioGeneration,
  storageChargeAllocation,
  isStoragePlant,
  buildCapitalCost
} from '../engine/advancedCapacity.js';
import { removePortfolioInstances } from '../engine/advancedGame.js';

export { isAdvancedMode };

// Grid slots the starting fleet occupies (section 16.15). Charged once,
// against Year 1: the year grants 4 new slots and the inherited fleet
// holds 2 of them, leaving 2 free. Later years grant their own slots as
// normal, because by then the fleet's slots are already spent.
//
// Without this the fourth Year 1 slot is a straight buff rather than
// compensation, and worse, retiring a legacy plant returns a slot that
// was never paid for - making retirement a net slot generator and
// wrecking the trade-off the mode is built on.
export function slotsHeldByStartingFleet(state) {
  if (state.year !== 1) return 0;
  // What is actually on the grid entering Year 1, which in a real game
  // is exactly the starting fleet: builds sit in pendingBuilds until
  // commit, and a pending retirement has not left the portfolio yet
  // (it adds its slot back separately). Reading the mode's configured
  // fleet instead would charge a blank grid for plants it never had.
  return state.portfolio.length;
}

export function slotsNewThisYear(state) {
  // Pending retirements refund their grid slot for this build phase
  // (section 16.11); pendingRetirements is always empty in Classic.
  // The slot table is per-mode: Advanced adds a fourth Year 1 slot to
  // pay for the two the legacy fleet occupies (section 16.15). Reading
  // the shared table here would have shown 3 while the reducer allowed
  // 4.
  return Math.max(
    0,
    (configForState(state).slotsNewPerYear[state.year] ?? 0) -
      slotsHeldByStartingFleet(state) +
      (state.pendingRetirements?.length ?? 0)
  );
}

export function slotsRemainingThisYear(state) {
  return Math.max(0, slotsNewThisYear(state) - state.pendingBuilds.length);
}

export function totalSlotsByEndOfYear(year, state = null) {
  const table = state
    ? configForState(state).slotsCumulativeByYear
    : SLOTS_CUMULATIVE_BY_YEAR;
  return table[year] ?? 0;
}

export function isSelectable(state, plantId) {
  if (state.phase !== PHASE.BUILDING) return false;
  if (!configForState(state).buildablePlantIds.has(plantId)) return false;
  return slotsRemainingThisYear(state) > 0;
}

export function currentDemand(state) {
  return configForState(state).demandForYear(state.year);
}

// Advanced Capacity Mode only; null in Classic.
export function currentPeakDemand(state) {
  return configForState(state).peakDemandForYear(state.year);
}

// Advanced Capacity Mode only; null in Classic.
export function currentRequiredFirmCapacity(state) {
  const peak = currentPeakDemand(state);
  return peak === null ? null : requiredFirmCapacity(peak);
}

export function disastersToDrawThisYear(state) {
  return DISASTER_DRAWS_PER_YEAR[state.year] ?? 0;
}

export function pendingBuildsAsPlants(state) {
  const { plantsById } = configForState(state);
  return state.pendingBuilds.map((id) => plantsById[id]);
}

export function portfolioAsPlants(state) {
  const { plantsById } = configForState(state);
  return state.portfolio.map((id) => plantsById[id]);
}

export function drawnDisastersThisYear(state) {
  const { disasterById } = configForState(state);
  return state.drawnDisasterIds.map((id) => disasterById[id]);
}

// The portfolio as it will stand once the pending build commits:
// current plants minus pending retirements plus pending builds.
export function plannedPortfolio(state) {
  const { portfolio } = removePortfolioInstances(
    state.portfolio,
    state.pendingRetirements ?? []
  );
  return [...portfolio, ...state.pendingBuilds];
}

// Advanced Capacity Mode retirement planning (section 16.11).
export function pendingRetirementsAsPlants(state) {
  const { plantsById } = configForState(state);
  return (state.pendingRetirements ?? []).map((id) => plantsById[id]);
}

export function isRetirable(state, plantId) {
  if (state.phase !== PHASE.BUILDING) return false;
  if (!configForState(state).supportsRetirement) return false;
  const owned = state.portfolio.filter((id) => id === plantId).length;
  const retiring = (state.pendingRetirements ?? []).filter(
    (id) => id === plantId
  ).length;
  return owned - retiring > 0;
}

export function pendingDecommissionCost(state) {
  const { plantsById } = configForState(state);
  return (state.pendingRetirements ?? []).reduce(
    (s, id) => s + decommissionFeeForPlant(plantsById[id], state.year),
    0
  );
}

// Charge held over from last year by inter-year carriers, in TWh per
// plant type. It costs no surplus this year (section 16.5.2).
export function carriedStorageCharge(state) {
  if (!isAdvancedMode(state)) return {};
  const last = state.yearResults[state.yearResults.length - 1];
  return last?.carriedChargeForNextYear ?? {};
}

// Unit count the carried charge was measured on, so a pending
// retirement scales it down rather than concentrating it (16.5.2).
export function carriedStorageChargeUnits(state) {
  if (!isAdvancedMode(state)) return {};
  const last = state.yearResults[state.yearResults.length - 1];
  return last?.carriedChargeUnitsForNextYear ?? {};
}

export function lastYearResult(state) {
  return state.yearResults[state.yearResults.length - 1] ?? null;
}

export function isGameOver(state) {
  return state.phase === PHASE.GAME_OVER;
}

export function persistentOutputMultiplierForPlant(state, plantId) {
  const config = configForState(state);
  const plant = config.plantsById[plantId];
  if (!plant) return 1;

  let multiplier = 1;
  for (const [disasterId, family] of Object.entries(
    state.persistentDisasterTargets ?? {}
  )) {
    const disaster = config.disasterById[disasterId];
    if (!disaster || plant.family !== family) continue;
    multiplier *= outputMultiplierFactorForPlant(disaster.effect, plantId);
  }
  return multiplier;
}

// Firm-capacity counterpart: persistent constraints hit the firm
// channel at the softened factor (section 16.6).
export function persistentFirmMultiplierForPlant(state, plantId) {
  const config = configForState(state);
  const plant = config.plantsById[plantId];
  if (!plant) return 1;

  let multiplier = 1;
  for (const [disasterId, family] of Object.entries(
    state.persistentDisasterTargets ?? {}
  )) {
    const disaster = config.disasterById[disasterId];
    if (!disaster || plant.family !== family) continue;
    multiplier *= firmFactorFromGenerationFactor(
      outputMultiplierFactorForPlant(disaster.effect, plantId)
    );
  }
  return multiplier;
}

export function projectedOutputRange(state) {
  const { plantsById } = configForState(state);
  const all = plannedPortfolio(state);
  let min = 0;
  let max = 0;
  let mean = 0;
  for (const id of all) {
    const plant = plantsById[id];
    if (isStoragePlant(plant)) continue;
    const multiplier = persistentOutputMultiplierForPlant(state, id);
    if (typeof plant.output === 'number') {
      min += plant.output * multiplier;
      max += plant.output * multiplier;
      mean += plant.output * multiplier;
    } else {
      min += plant.output.min * multiplier;
      max += plant.output.max * multiplier;
      mean += ((plant.output.min + plant.output.max) / 2) * multiplier;
    }
  }
  return { min, max, mean };
}

// Advanced Capacity Mode planning view: firm capacity of the current
// portfolio plus pending builds. Mirrors the engine rules — fixed
// ex-ante credit, persistent constraint multipliers (the only
// disaster effects known at build time), and the expected-surplus
// storage charging rule against base annual demand.
export function projectedFirmCapacity(state) {
  if (!isAdvancedMode(state)) return null;
  const config = configForState(state);
  const all = plannedPortfolio(state);

  const expectedGeneration = expectedPortfolioGeneration(
    all,
    config.plantsById
  );
  const expectedSurplus =
    expectedGeneration - config.demandForYear(state.year);
  const allocation = storageChargeAllocation({
    expectedSurplus,
    portfolio: all,
    plantsById: config.plantsById,
    carriedCharge: carriedStorageCharge(state),
    carriedChargeUnits: carriedStorageChargeUnits(state)
  });
  const chargeLevels = {};
  for (const row of allocation) chargeLevels[row.id] = row.level;

  const diversityUplift = variableDiversityUplift(all, config.plantsById);

  // Split three ways so the build screen can say where each GW comes
  // from. The diversity bonus is otherwise invisible: it lands inside
  // every variable plant's credit, so the player sees only that the
  // total moved and reads it as one plant being better than another.
  // Weight per type rather than counting instances, so the declining
  // schedule inside a variable family lands here exactly as it does in
  // the engine. Under the flat default a weight IS the count, so this
  // is unchanged arithmetic.
  const firmWeights = firmCapacityCountWeights({
    portfolio: all,
    plantsById: config.plantsById
  });
  let fromPlants = 0;
  let fromDiversity = 0;
  let fromStorage = 0;
  for (const id of new Set(all)) {
    const plant = config.plantsById[id];
    const persistent = persistentFirmMultiplierForPlant(state, id);
    const weight = firmWeights[id] ?? 0;
    if (isStoragePlant(plant)) {
      const firm =
        upliftedFirmCapacityUnits(plant, diversityUplift) *
        (chargeLevels[id] ?? 0);
      fromStorage += firm * persistent * weight;
      continue;
    }
    // upliftedFirmCapacityUnits with an uplift of 0 is the base credit,
    // so the difference is exactly what diversity bought.
    const base = upliftedFirmCapacityUnits(plant, 0);
    const uplifted = upliftedFirmCapacityUnits(plant, diversityUplift);
    fromPlants += base * persistent * weight;
    fromDiversity += (uplifted - base) * persistent * weight;
  }
  const total = fromPlants + fromDiversity + fromStorage;

  const variableFamilies = [...variableFamiliesOwned(all, config.plantsById)];
  const variableFamiliesAvailable = variableFamiliesOwned(
    Object.keys(config.plantsById),
    config.plantsById
  ).size;

  // One row per storage type for the build-phase charge warning: how
  // full it will be, and the extra surplus that would top it up. The
  // engine computes surplusShort against the charging queue, so a
  // store waiting behind another reports the whole gap, not just its
  // own (section 16.5.1).
  const storageRows = allocation.map(({ id, name, count, level, surplusShort }) => ({
    id,
    name,
    count,
    level,
    surplusShort
  }));

  return {
    total,
    fromPlants,
    fromDiversity,
    fromStorage,
    hasStorage: all.some((id) => isStoragePlant(config.plantsById[id])),
    chargeLevels,
    expectedSurplus,
    diversityUplift,
    variableFamilies,
    variableFamiliesAvailable,
    storageRows
  };
}

// What a candidate plant's firm capacity would actually be if it were
// built now. The plant card otherwise shows the bare technology
// rating, which ignores the diversity bonus entirely — so a plant that
// would open a new variable family looks identical to one that would
// not, even though only the first raises the credit of every variable
// plant already in the plan.
export function prospectiveFirmCapacity(state, plant) {
  if (!isAdvancedMode(state) || !plant) return null;
  const config = configForState(state);
  const planned = plannedPortfolio(state);

  const upliftBefore = variableDiversityUplift(planned, config.plantsById);
  const familiesBefore = variableFamiliesOwned(planned, config.plantsById).size;
  const withPlant = [...planned, plant.id];
  const upliftAfter = variableDiversityUplift(withPlant, config.plantsById);
  const familiesAfter = variableFamiliesOwned(withPlant, config.plantsById).size;

  // Storage is never uplifted, so effective collapses to base for it.
  const base = upliftedFirmCapacityUnits(plant, 0);

  // Under the declining schedule a plant's own contribution depends on
  // how many of its family are already planned, so `effective` is its
  // MARGINAL credit rather than its sticker rating: the weight this id
  // gains by adding one more copy. Copies of one id are identical, so
  // the weight delta is the whole story for it.
  const weightsBefore = firmCapacityCountWeights({
    portfolio: planned,
    plantsById: config.plantsById
  });
  const weightsAfter = firmCapacityCountWeights({
    portfolio: withPlant,
    plantsById: config.plantsById
  });
  const weightDelta =
    (weightsAfter[plant.id] ?? 0) - (weightsBefore[plant.id] ?? 0);
  const diversityAdjusted = upliftedFirmCapacityUnits(plant, upliftAfter);
  const effective = diversityAdjusted * weightDelta;

  // Whatever the whole plan gains beyond that is what the new plant did
  // to the plants already in it: opening a new family lifts every
  // variable plant's credit, and adding to a crowded family can demote
  // its neighbours. Both are invisible on the card otherwise, and the
  // first is the larger half of the gain.
  const totalFirmOf = (portfolio, uplift, weights) => {
    let total = 0;
    for (const id of new Set(portfolio)) {
      const owned = config.plantsById[id];
      if (isStoragePlant(owned)) continue;
      total += upliftedFirmCapacityUnits(owned, uplift) * (weights[id] ?? 0);
    }
    return total;
  };
  const totalDelta =
    totalFirmOf(withPlant, upliftAfter, weightsAfter) -
    totalFirmOf(planned, upliftBefore, weightsBefore);
  const fleetGain = isStoragePlant(plant) ? 0 : totalDelta - effective;

  return {
    base,
    effective,
    fleetGain,
    diversityBonus: diversityAdjusted - base,
    familyCreditFactor: plant.type === 'variable' ? weightDelta : 1,
    addsFamily: familiesAfter > familiesBefore,
    familiesAfter
  };
}

function priorPlayerBuilds(state) {
  return Object.entries(state.buildPlanByYear ?? {})
    .filter(([year]) => Number(year) < state.year)
    .flatMap(([, builds]) => builds ?? []);
}

export function prospectiveBuildCapital(state, plantId) {
  const { plantsById } = configForState(state);
  const priorBuilds = [
    ...priorPlayerBuilds(state),
    ...(state.pendingBuilds ?? [])
  ];
  return buildCapitalCost({
    newlyBuilt: [plantId],
    priorPlayerBuilds: priorBuilds,
    plantsById
  });
}

export function liveBuildSummary(state) {
  const { plantsById, eosDuplicateMultiplier, eosFirstCopyPaysFull } =
    configForState(state);
  const advanced = isAdvancedMode(state);
  const fullPortfolio = plannedPortfolio(state);
  const currentEmissions = fullPortfolio.reduce(
    (sum, id) => sum + plantsById[id].emissions,
    0
  );
  // Advanced Mode brackets use year-weighted emissions
  // (section 16.10); Classic uses raw cumulative emissions.
  const emissionWeightFor = (year) =>
    advanced ? ADVANCED_CARBON_YEAR_WEIGHTS[year] ?? 1 : 1;
  const pastEmissions = state.yearResults.reduce(
    (sum, r) => sum + r.emissions,
    0
  );
  const pastWeightedEmissions = state.yearResults.reduce(
    (sum, r) => sum + r.emissions * emissionWeightFor(r.year),
    0
  );
  const cumulativeEmissions = pastEmissions + currentEmissions;
  const bracket = lookupCarbonBracket(
    Math.round(
      pastWeightedEmissions + currentEmissions * emissionWeightFor(state.year)
    )
  );
  const priorBuilds = priorPlayerBuilds(state);
  const capitalBreakdown = advanced
    ? buildCapitalCost({
        newlyBuilt: state.pendingBuilds,
        priorPlayerBuilds: priorBuilds,
        plantsById
      })
    : {
        listedCapital: state.pendingBuilds.reduce(
          (sum, id) => sum + plantsById[id].capital,
          0
        ),
        resourceDevelopmentPremium: 0,
        effectiveCapital: state.pendingBuilds.reduce(
          (sum, id) => sum + plantsById[id].capital,
          0
        )
      };
  const capitalThisYear = capitalBreakdown.effectiveCapital;
  const decommissionThisYear = pendingDecommissionCost(state);
  const pastCosts = state.yearResults.reduce(
    (sum, r) =>
      sum +
      r.capital +
      (r.resourceDevelopmentPremium ?? 0) +
      r.opex +
      r.shortfallCost +
      (r.capacityShortfallCost ?? 0) +
      (r.decommissionCost ?? 0),
    0
  );
  const totalExpenditure = pastCosts + capitalThisYear + decommissionThisYear;
  const opexRows = computeYearOpex({
    portfolio: fullPortfolio,
    plantsById,
    subsidyActive: state.subsidyActive,
    priceShockActive: false,
    eosDuplicateMultiplier,
    eosFirstCopyPaysFull
  });
  const annualOpex = totalYearOpex(opexRows);
  return {
    cumulativeEmissions,
    bracket,
    capitalThisYear,
    listedCapitalThisYear: capitalBreakdown.listedCapital,
    resourceDevelopmentPremiumThisYear:
      capitalBreakdown.resourceDevelopmentPremium,
    decommissionThisYear,
    annualOpex,
    totalExpenditure
  };
}
